"use client";

import { Button } from "@/components/ui/button";
import Link from "next/link";
import posthog from "posthog-js";
import { Suspense, useEffect } from "react";

// This component will be wrapped in Suspense
const ErrorContent = ({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) => {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-4 text-center">
      <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100">Something went wrong</h1>
      <p className="mt-4 text-gray-600 dark:text-gray-400">
        We&apos;re sorry, but there was an error processing your request.
      </p>
      {error.digest && <p className="mt-2 text-sm text-gray-500 dark:text-gray-500">Error ID: {error.digest}</p>}
      <div className="mt-8 flex flex-col gap-4 sm:flex-row">
        <Button onClick={reset} className="bg-blue-600 hover:bg-blue-700">
          Try again
        </Button>
        <Link href="/">
          <Button variant="outline" className="bg-gray-200 text-gray-800 hover:bg-gray-300">
            Return Home
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default function Error({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) {
  useEffect(() => {
    // Log the error to PostHog
    console.error("Root error boundary caught:", error);
    if (posthog?.__loaded) {
      posthog.captureException(error, {
        $current_url: window.location.href, // Add current URL for context
        digest: error.digest // Include the digest if available
      });
    }
  }, [error]); // Re-run the effect if the error changes

  return (
    <Suspense fallback={<div className="flex min-h-screen items-center justify-center">Loading...</div>}>
      <ErrorContent error={error} reset={reset} />
    </Suspense>
  );
}
